import type {
  DeferredLinesBySupplier,
  EnrichedDeferredLine,
  EnrichedLine,
} from "../../services/distributionPreviewService";

/**
 * Auditoría 2026-05-05 — la lista principal de líneas activas también se
 * agrupa por proveedor. Mismo shape que `DeferredLinesBySupplier` pero
 * con `EnrichedLine[]` (con columnas distintas: source, denial, etc.).
 */
export interface ActiveLinesBySupplier {
  supplier_id: number | null;
  supplier_name: string | null;
  total_lines: number;
  total_units: number;
  lines: EnrichedLine[];
}

/**
 * Helper de filtrado del panel "Líneas diferidas por proveedor".
 *
 * Sintaxis igual al toolbar de Forecast de Compras:
 *   - Coma separa términos OR (`duna, tramontina` matchea filas con duna O tramontina).
 *   - Prefijo `-` excluye (`-mesa` excluye filas que contengan mesa).
 *   - Vacío = sin filtro (devuelve buckets/líneas sin tocar).
 *
 * Filtros aplicados:
 *   - `supplierQuery`: matchea contra `supplier_name` del bucket. Solo
 *     buckets que pasen el filtro entran al resultado. Útil para
 *     "yuliana, tramontina" → solo esos proveedores.
 *   - `productQuery`: matchea contra `product_name` + `unified_product_id`
 *     de las líneas hijas. Filtra dentro de cada bucket; buckets que
 *     queden sin líneas tras el filtro se eliminan. Los totales
 *     (`total_lines`, `total_units`) se recalculan sobre las líneas
 *     que sobreviven.
 *
 * Pure function — el componente la envuelve en useMemo.
 */

export interface ParsedQuery {
  include: string[];
  exclude: string[];
}

export function parseSearchQuery(term: string): ParsedQuery {
  if (!term) return { include: [], exclude: [] };
  const raw = term
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const include = raw.filter((t) => !t.startsWith("-"));
  const exclude = raw
    .filter((t) => t.startsWith("-"))
    .map((t) => t.slice(1).trim())
    .filter(Boolean);
  return { include, exclude };
}

export function matchesQuery(text: string, parsed: ParsedQuery): boolean {
  const lower = text.toLowerCase();
  const matchesInclude =
    parsed.include.length === 0 ||
    parsed.include.some((q) => lower.includes(q));
  const matchesExclude =
    parsed.exclude.length > 0 &&
    parsed.exclude.some((q) => lower.includes(q));
  return matchesInclude && !matchesExclude;
}

function lineSearchText(
  line: EnrichedDeferredLine | EnrichedLine,
): string {
  if (line.product_name) {
    return `${line.product_name} ${line.unified_product_id}`;
  }
  return line.unified_product_id;
}

export function filterBucketsByQuery(
  buckets: DeferredLinesBySupplier[],
  supplierQuery: string,
  productQuery: string,
): DeferredLinesBySupplier[] {
  const supplierParsed = parseSearchQuery(supplierQuery);
  const productParsed = parseSearchQuery(productQuery);
  const noSupplierFilter =
    supplierParsed.include.length === 0 && supplierParsed.exclude.length === 0;
  const noProductFilter =
    productParsed.include.length === 0 && productParsed.exclude.length === 0;
  if (noSupplierFilter && noProductFilter) return buckets;

  const result: DeferredLinesBySupplier[] = [];
  for (const bucket of buckets) {
    if (!noSupplierFilter) {
      const supplierText = bucket.supplier_name ?? "Sin proveedor asignado";
      if (!matchesQuery(supplierText, supplierParsed)) continue;
    }
    if (noProductFilter) {
      result.push(bucket);
      continue;
    }
    const matchedLines = bucket.lines.filter((line) =>
      matchesQuery(lineSearchText(line), productParsed),
    );
    if (matchedLines.length === 0) continue;
    const totalUnits = matchedLines.reduce((acc, l) => acc + l.qty_planned, 0);
    result.push({
      ...bucket,
      total_lines: matchedLines.length,
      total_units: totalUnits,
      lines: matchedLines,
    });
  }
  return result;
}

/**
 * Agrupa líneas activas por proveedor (mismo pattern que el backend
 * `groupDeferredBySupplier`). Calculado en cliente para no requerir
 * re-llamada al backend; el snapshot del preview viene plano.
 *
 * `total_lines`/`total_units` cuentan SOLO líneas no excluidas (excluidas
 * por `excludedSet` no entran al envío). Si el bucket queda con 0 líneas
 * eligibles, se mantiene visible para que el operador pueda re-incluir.
 */
export function groupActiveLinesBySupplier(
  lines: EnrichedLine[],
  excludedIds: Set<string>,
): ActiveLinesBySupplier[] {
  const buckets = new Map<string, ActiveLinesBySupplier>();
  for (const line of lines) {
    const key = line.supplier_name ?? "_null";
    const existing = buckets.get(key);
    const isExcluded = excludedIds.has(line.recommendation_id);
    if (existing) {
      existing.lines.push(line);
      if (!isExcluded) {
        existing.total_lines += 1;
        existing.total_units += line.qty_planned;
      }
    } else {
      buckets.set(key, {
        supplier_id: line.supplier_id,
        supplier_name: line.supplier_name,
        total_lines: isExcluded ? 0 : 1,
        total_units: isExcluded ? 0 : line.qty_planned,
        lines: [line],
      });
    }
  }
  return Array.from(buckets.values()).sort((a, b) => {
    if (a.supplier_name === null && b.supplier_name !== null) return 1;
    if (a.supplier_name !== null && b.supplier_name === null) return -1;
    return b.total_units - a.total_units;
  });
}

/**
 * Filtra buckets de activas con la misma sintaxis que el panel de diferidas.
 */
export function filterActiveLinesByQuery(
  buckets: ActiveLinesBySupplier[],
  supplierQuery: string,
  productQuery: string,
): ActiveLinesBySupplier[] {
  const supplierParsed = parseSearchQuery(supplierQuery);
  const productParsed = parseSearchQuery(productQuery);
  const noSupplierFilter =
    supplierParsed.include.length === 0 && supplierParsed.exclude.length === 0;
  const noProductFilter =
    productParsed.include.length === 0 && productParsed.exclude.length === 0;
  if (noSupplierFilter && noProductFilter) return buckets;

  const result: ActiveLinesBySupplier[] = [];
  for (const bucket of buckets) {
    if (!noSupplierFilter) {
      const supplierText = bucket.supplier_name ?? "Sin proveedor asignado";
      if (!matchesQuery(supplierText, supplierParsed)) continue;
    }
    if (noProductFilter) {
      result.push(bucket);
      continue;
    }
    const matchedLines = bucket.lines.filter((line) =>
      matchesQuery(lineSearchText(line), productParsed),
    );
    if (matchedLines.length === 0) continue;
    result.push({
      ...bucket,
      lines: matchedLines,
    });
  }
  return result;
}
